import React, { useEffect, useState } from "react";
import { api, ApiError } from "../../../api/apiClient";
import { useAuth } from "../../../context/AuthContext";
import UserPartyLink from "../../../components/UserPartyLink";
import PartySearch from "../../components/PartySearch";
import PartySummary from "../../components/PartySummary";
import { Party } from "../../types/types";

import {
  RolesLookup,
  TenantOption,
  CreatePlatformUserRequest,
  UpdatePlatformUserRequest,
  PlatformUserDto,
  ServiceAccessOption,
} from "../../types/types";
import { TENANT_ROLE_CODES } from "../../../constants/constants";

import "../../../css/buttons.css";
import "../../../css/forms.css";
import "./UpsertPlatformUserTab.css";

interface CreateUserTabProps {
  userId?: string | null;
  onCreated?: () => void;
  onCancel?: () => void;
  initialTenantId?: number;
}

type TenantRoleSelections = Record<number, string[]>;
type TenantServiceSelections = Record<number, number[]>;

export default function CreateUserTab({
  userId,
  onCreated,
  onCancel,
  initialTenantId,
}: CreateUserTabProps) {
  const { isSystemAdmin, selectedTenant, user } = useAuth();

  const isEditMode = !!userId;
  const isOrganisationScoped = Boolean(initialTenantId);

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [partyMode, setPartyMode] = useState<"create" | "link">("create");
  const [selectedParty, setSelectedParty] = useState<Party | null>(null);

  const [isSystemUser, setIsSystemUser] = useState(false);

  const [rolesLookup, setRolesLookup] = useState<RolesLookup | null>(null);
  const [tenants, setTenants] = useState<TenantOption[]>([]);

  const [selectedSystemRoles, setSelectedSystemRoles] = useState<string[]>([]);
  const [tenantRoleSelections, setTenantRoleSelections] =
    useState<TenantRoleSelections>({});
  const [tenantServiceSelections, setTenantServiceSelections] =
    useState<TenantServiceSelections>({});
  const [serviceOptions, setServiceOptions] =
    useState<Record<number, ServiceAccessOption[]>>({});

  const [loadingRoles, setLoadingRoles] = useState(true);
  const [loadingTenants, setLoadingTenants] = useState(true);

  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadStaticData();
  }, []);

  useEffect(() => {
    if (initialTenantId && !userId) {
      setTenantRoleSelections((current) =>
        Object.prototype.hasOwnProperty.call(current, initialTenantId)
          ? current
          : { ...current, [initialTenantId]: [] },
      );
    }
  }, [initialTenantId, userId]);

  useEffect(() => {
    if (!isSystemAdmin && selectedTenant) {
      setTenantRoleSelections((current) =>
        Object.prototype.hasOwnProperty.call(current, selectedTenant.tenantId)
          ? current
          : { [selectedTenant.tenantId]: [] },
      );
    }
  }, [isSystemAdmin, selectedTenant]);

  useEffect(() => {
    if (userId) {
      loadUser();
    }
  }, [userId]);

  useEffect(() => {
    Object.keys(tenantRoleSelections).forEach((tenantId) => {
      const id = Number(tenantId);
      if (!serviceOptions[id]) {
        void loadServiceOptions(id);
      }
    });
  }, [tenantRoleSelections]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadServiceOptions(tenantId: number) {
    try {
      const options = await api<ServiceAccessOption[]>(
        `/api/platformuser/tenant-services/${tenantId}`,
      );
      setServiceOptions((current) => ({ ...current, [tenantId]: options }));
    } catch (e) {
      setError((e as ApiError).message);
    }
  }

  async function loadStaticData() {
    try {
      const roles = await api<RolesLookup>("/api/StaticData/roles");
      const tenants = await api<TenantOption[]>("/api/StaticData/tenants");

      setRolesLookup(roles);
      setTenants(tenants);
    } catch (e) {
      setError((e as ApiError).message);
    } finally {
      setLoadingRoles(false);
      setLoadingTenants(false);
    }
  }

  async function loadUser() {
    try {
      const u = await api<PlatformUserDto>(
        `/api/platformuser/get-user/${userId}`,
      );

      setEmail(u.email ?? "");
      setFirstName(u.firstName ?? "");
      setLastName(u.lastName ?? "");

      if (!isOrganisationScoped && u.roles?.length) {
        setIsSystemUser(true);
        setSelectedSystemRoles(u.roles);
      }

      const assignments =
        u.tenants?.length > 0
          ? u.tenants
          : u.tenantId
            ? [
                {
                  tenantId: u.tenantId,
                  tenantName: u.tenantName ?? "",
                  roles: u.tenantRoleCodes ?? [],
                  serviceIds: [],
                },
              ]
            : [];

      setTenantRoleSelections(
        Object.fromEntries(
          assignments
            .filter(
              (assignment) =>
                (isSystemAdmin && !isOrganisationScoped) ||
                assignment.tenantId ===
                  (initialTenantId ?? selectedTenant?.tenantId),
            )
            .map((assignment) => [
              assignment.tenantId,
              [...assignment.roles],
            ]),
        ),
      );
      setTenantServiceSelections(
        Object.fromEntries(assignments.map((assignment) => [
          assignment.tenantId,
          [...(assignment.serviceIds ?? [])],
        ])),
      );
    } catch (e) {
      setError((e as ApiError).message);
    }
  }

  function validateForm(): string | null {
    if (!email) return "Email is required";
    if (!isEditMode && partyMode === "link" && selectedParty?.partyType !== "Person")
      return "Select an existing person to link to this login";

    if (isSystemUser && selectedSystemRoles.length === 0)
      return "System users must have at least one system role";

    const tenantAssignments = buildTenantAssignments();
    if (
      isOrganisationScoped &&
      initialTenantId &&
      (tenantRoleSelections[initialTenantId]?.length ?? 0) === 0
    )
      return "Select at least one organisation role";

    if (!isSystemUser && tenantAssignments.length === 0)
      return "Tenant users must have at least one tenant";

    if (
      !isSystemUser &&
      tenantAssignments.some((assignment) => assignment.roles.length === 0)
    )
      return "Every selected tenant must have at least one role";

    if (tenantAssignments.some((assignment) =>
      assignment.roles.includes(TENANT_ROLE_CODES.SERVICE_MANAGER) &&
      assignment.serviceIds.length === 0))
      return "Assign every Service Manager to at least one service";

    if (tenantAssignments.some((assignment) =>
      assignment.roles.includes(TENANT_ROLE_CODES.COMMUNICATIONS_SENDER) &&
      !assignment.roles.includes(TENANT_ROLE_CODES.SERVICE_MANAGER)))
      return "Communications Sender requires the Service Manager role";

    return null;
  }

  function buildTenantAssignments() {
    return Object.entries(tenantRoleSelections).map(([tenantId, roles]) => ({
      tenantId: Number(tenantId),
      roles,
      serviceIds: tenantServiceSelections[Number(tenantId)] ?? [],
    }));
  }

  function buildCreateRequest(): CreatePlatformUserRequest {
    return {
      partyId: partyMode === "link" ? selectedParty?.partyId : undefined,
      email,
      firstName,
      lastName,
      systemRoles: isSystemUser ? selectedSystemRoles : [],
      tenants: isSystemUser ? [] : buildTenantAssignments(),
    };
  }

  function buildUpdateRequest(): UpdatePlatformUserRequest {
    return {
      userId: userId!,
      firstName,
      lastName,
      systemRoles: isOrganisationScoped
        ? undefined
        : isSystemAdmin
        ? isSystemUser
          ? selectedSystemRoles
          : []
        : undefined,
      tenants: isSystemUser ? [] : buildTenantAssignments(),
    };
  }

  async function createUser() {
    await api<void>("/api/PlatformUser/create-user", {
      method: "POST",
      body: buildCreateRequest(),
    });
  }

  async function updateUser() {
    await api<void>(`/api/platformuser/update-user/${userId}`, {
      method: "PUT",
      body: buildUpdateRequest(),
    });
  }

  const handleSubmit = async () => {
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);

    try {
      if (isEditMode) {
        await updateUser();
      } else {
        await createUser();
      }

      onCreated?.();
    } catch (e) {
      setError((e as ApiError).message ?? "Failed to save user");
    } finally {
      setSaving(false);
    }
  };

  const toggleRole = (
    roleCode: string,
    setter: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    setter((prev) =>
      prev.includes(roleCode)
        ? prev.filter((r) => r !== roleCode)
        : [...prev, roleCode],
    );
  };

  const renderServiceAssignments = (tenantId: number) => {
    const selectedIds = tenantServiceSelections[tenantId] ?? [];
    return (
      <div className="service-access-options">
        <div className="form-label">Assigned services</div>
        <p className="field-hint">The linked person is enrolled in these services. Login permissions depend on their organisation roles.</p>
        {(serviceOptions[tenantId] ?? []).map((service) => (
          <label key={service.serviceId} className="option-item">
            <input
              type="checkbox"
              checked={selectedIds.includes(service.serviceId)}
              onChange={() => setTenantServiceSelections((current) => ({
                ...current,
                [tenantId]: selectedIds.includes(service.serviceId)
                  ? selectedIds.filter((id) => id !== service.serviceId)
                  : [...selectedIds, service.serviceId],
              }))}
            />
            {service.name}
          </label>
        ))}
        {(serviceOptions[tenantId] ?? []).length === 0 && (
          <p className="field-hint">No active services are available.</p>
        )}
      </div>
    );
  };

  if (loadingRoles || loadingTenants || !user) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      {/* USER DETAILS */}
      <div className="card">
        <div className="card-header"><div className="card-title">Personal Details</div></div>

        <div className="form-row">
          <div className="form-group form-group--full">
            <label className="form-label">Email</label>
            <input
              className={`form-input${isEditMode ? " user-login-email" : ""}`}
              value={email}
              disabled={isEditMode}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">First Name</label>
            <input
              className="form-input"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Last Name</label>
            <input
              className="form-input"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
        </div>

        {isSystemAdmin && !isOrganisationScoped && (
          <div className="toggle-container">
            <label className="toggle-label-text">System user</label>

            <div className="toggle-wrapper">
              <input
                type="checkbox"
                checked={isSystemUser}
                onChange={(e) => {
                  setIsSystemUser(e.target.checked);
                  setTenantRoleSelections({});
                  setSelectedSystemRoles([]);
                }}
                className="toggle-checkbox"
              />
              <label className="toggle-background" />
            </div>
          </div>
        )}
      </div>

      {userId && <UserPartyLink key={userId} userId={userId} showPersonalDetails={false} />}
      {!userId && <div className="card">
        <div className="card-header"><div className="card-title">Party Record</div></div>
        <div className="form-row"><div className="form-group form-group--full">
          <label className="form-label" htmlFor="admin-party-mode">Person record</label>
          <select id="admin-party-mode" className="form-input" value={partyMode} onChange={(event) => { setPartyMode(event.target.value as "create" | "link"); setSelectedParty(null); }}>
            <option value="create">Create a person from login details</option>
            <option value="link">Link an existing person</option>
          </select>
        </div></div>
        {partyMode === "link" ? <>
          <PartySearch searchLabel="Find an existing person" onPartySelected={setSelectedParty} />
          {selectedParty?.partyType === "Person" && <PartySummary key={selectedParty.partyId} partyId={selectedParty.partyId} />}
          {selectedParty && selectedParty.partyType !== "Person" && <p role="alert">Select a person record.</p>}
        </> : <p className="field-hint">A person is created using the login name and email. An existing person with the same email is reused to avoid duplicates.</p>}
      </div>}

      {isOrganisationScoped && initialTenantId && rolesLookup && (
        <div className="card">
          <div className="card-title">Organisation Roles</div>
          <p className="field-hint">
            Select the roles this user has in the active organisation.
          </p>

          <div className="tenant-assignment-roles organisation-role-options">
            {rolesLookup.tenantRoles.map((role) => (
              <label key={role.code} className="option-item">
                <input
                  type="checkbox"
                  checked={(
                    tenantRoleSelections[initialTenantId] ?? []
                  ).includes(role.code)}
                  onChange={() =>
                    setTenantRoleSelections((current) => ({
                      ...current,
                      [initialTenantId]: (
                        current[initialTenantId] ?? []
                      ).includes(role.code)
                        ? current[initialTenantId].filter(
                            (code) => code !== role.code,
                          )
                        : [...(current[initialTenantId] ?? []), role.code],
                    }))
                  }
                />
                {role.name}
              </label>
            ))}
          </div>
          {renderServiceAssignments(initialTenantId)}
        </div>
      )}

      {/* SYSTEM ROLES */}
      {!isOrganisationScoped && isSystemUser && rolesLookup && (
        <div className="card">
          <div className="card-title">System Roles</div>

          {rolesLookup.systemRoles.map((role) => (
            <label key={role.code}>
              <input
                type="checkbox"
                checked={selectedSystemRoles.includes(role.code)}
                onChange={() => toggleRole(role.code, setSelectedSystemRoles)}
              />
              {role.name}
            </label>
          ))}
        </div>
      )}

      {/* TENANT DETAILS */}
      {!isOrganisationScoped && !isSystemUser && (
        <div className="card">
          <div className="card-title">Tenant Details</div>

          <p className="field-hint">
            Select each tenant the user belongs to, then choose one or more
            roles for that tenant.
          </p>

          <div className="tenant-assignment-list">
            {tenants.map((tenant) => {
              const selected = Object.prototype.hasOwnProperty.call(
                tenantRoleSelections,
                tenant.tenantId,
              );
              const selectedRoles =
                tenantRoleSelections[tenant.tenantId] ?? [];

              return (
                <div className="tenant-assignment-item" key={tenant.tenantId}>
                  <label className="tenant-assignment-heading">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={(event) =>
                        setTenantRoleSelections((current) => {
                          if (event.target.checked) {
                            return {
                              ...current,
                              [tenant.tenantId]:
                                current[tenant.tenantId] ?? [],
                            };
                          }

                          const next = { ...current };
                          delete next[tenant.tenantId];
                          return next;
                        })
                      }
                    />
                    <span>{tenant.name}</span>
                  </label>

                  {selected && rolesLookup && (
                    <div className="tenant-assignment-roles">
                      {rolesLookup.tenantRoles.map((role) => (
                        <label key={role.code} className="option-item">
                          <input
                            type="checkbox"
                            checked={selectedRoles.includes(role.code)}
                            onChange={() =>
                              setTenantRoleSelections((current) => ({
                                ...current,
                                [tenant.tenantId]: current[
                                  tenant.tenantId
                                ]?.includes(role.code)
                                  ? current[tenant.tenantId].filter(
                                      (code) => code !== role.code,
                                    )
                                  : [
                                      ...(current[tenant.tenantId] ?? []),
                                      role.code,
                                    ],
                              }))
                            }
                          />
                          {role.name}
                        </label>
                      ))}
                      {renderServiceAssignments(tenant.tenantId)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {error && <div className="error">{error}</div>}

      <div className="form-actions">
        <button
          className="form-submit-button"
          onClick={handleSubmit}
          disabled={saving}
        >
          Save
        </button>

        {onCancel && (
          <button className="form-submit-button" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
